/**
 * useGanttImperativeSync.ts
 * BUG-2026-0093 — Imperative update path for the Master Gantt chart.
 *
 * Why this exists
 * ----------------
 * The Master Gantt froze and OOM-crashed after a few "Toggle Proposals" clicks.
 * Root cause (confirmed against the Highcharts 12.5 source + live measurement):
 *
 *   - highcharts-react-official with `immutable={false}` calls
 *     `chart.update(options, true, true)` whenever the `options` identity
 *     changes.
 *   - `Chart.update()` reconciles its collections and calls `yAxis.update()`,
 *     which is `axis.destroy(true); axis.init()` (Axis.js).
 *   - For a TREEGRID axis, `init` re-runs `TreeGridAxis.wrapInit`, which does
 *     `addEvent(chart, 'beforeRedraw', onBeforeRender)` with NO de-duplication.
 *     `destroy(true)` only clears the axis's own listeners, not these
 *     chart-level ones — so every `chart.update()` STACKS another
 *     `onBeforeRender`.
 *   - `onBeforeRender` rebuilds the whole tree and calls `series.setData()` for
 *     each series. With K stacked copies it runs K times per redraw, so the
 *     rendered points / rows / ticks multiply super-linearly (measured:
 *     200 → 25,743 SVG nodes in three ON-toggles) → main-thread freeze → OOM.
 *
 * The fix is to stop pushing the full options object through `chart.update()`.
 * `setData`, `setExtremes`, `addPlotLine`/`removePlotLine` and `setSize` all
 * update the chart in place WITHOUT re-initialising the yAxis, so the single
 * creation-time `onBeforeRender` listener stays put and nothing accumulates.
 *
 * The owning component renders <HighchartsReact allowChartUpdate={false} …/> so
 * the wrapper creates the chart exactly once and never calls `chart.update`
 * itself; this hook is then the sole driver of subsequent changes.
 */

import { useLayoutEffect, useRef } from 'react';
import type { MutableRefObject, RefObject } from 'react';
import Highcharts from 'highcharts/highstock';
import type HighchartsReact from 'highcharts-react-official';
import type { GanttPoint } from './masterGanttRowBuilder.js';
import type { GanttRowHeightController } from './ganttRowHeightSync.js';

/** User's last navigator/scrollbar zoom, captured by afterSetExtremes. */
export type UserExtremes = { min: number; max: number } | null;

/**
 * BUG-2026-0207 (rework) — the two navigator instance flags Highcharts Stock
 * sets from the base series' `updatedData` event and consumes in its
 * `foundExtremes` hook (Navigator#updatedDataHandler /
 * Navigator#modifyBaseAxisExtremes). Not part of the public typings, hence the
 * narrow structural type — the fields have been stable since Highstock 6.
 */
type NavigatorStickFlags = { stickToMin?: boolean | null; stickToMax?: boolean | null };
type ChartWithNavigator = Highcharts.Chart & { navigator?: NavigatorStickFlags };

export interface GanttSyncParams {
  /** Points for the single Gantt series (treegrid hierarchy via id/parent). */
  seriesData: GanttPoint[];
  /** xAxis window start in Unix ms (UTC midnight). */
  xMin: number;
  /** xAxis window end in Unix ms (UTC midnight). */
  xMax: number;
  /**
   * Today + Critical-Path plot lines. Each MUST carry a stable `id` so this
   * hook can reconcile them across renders without re-initialising the axis.
   */
  plotLines: Highcharts.AxisPlotLinesOptions[];
  /**
   * Weekend + stat-holiday shading bands. Each MUST carry a stable `id` so this
   * hook can reconcile them across renders (range/holiday changes) the same way
   * it reconciles plot lines — never via xAxis.update() (which destroy+inits the
   * axis and re-stacks the treegrid beforeRedraw listener — BUG-2026-0093).
   */
  plotBands: Highcharts.AxisPlotBandsOptions[];
  /**
   * Desired chart height in px. With a `rowHeightController` this is the
   * VIEWPORT (max) height — the controller sizes the chart to its visible rows
   * and never past this (BUG-2026-0194); without one it is the exact height.
   */
  height: number;
  /**
   * BUG-2026-0194 — fixed-row-height controller (ganttRowHeightSync). When
   * present, height changes are handed to it and applied by its `render` hook
   * after the redraw, instead of a direct `setSize(height)`.
   */
  rowHeightController?: GanttRowHeightController | null;
}

/**
 * Imperatively sync an already-created Highcharts Gantt chart to new data,
 * range, plot lines and height — never via `chart.update(fullOptions)`.
 *
 * @param chartRef         HighchartsReact ref holding the live chart instance.
 * @param params           The current data/range/plotlines/height to apply.
 * @param userExtremesRef  Shared ref the chart's afterSetExtremes handler writes
 *                         the user's zoom into; read here to restore it across
 *                         data refreshes (BUG-2026-0076).
 */
export function useGanttImperativeSync(
  chartRef: RefObject<HighchartsReact.RefObject | null>,
  params: GanttSyncParams,
  userExtremesRef: MutableRefObject<UserExtremes>,
): void {
  const { seriesData, xMin, xMax, plotLines, plotBands, height, rowHeightController = null } = params;

  // Last range we applied — distinguishes a genuine date-range change (snap the
  // view, discard saved zoom) from a data refresh (preserve the user's zoom).
  const prevRangeRef = useRef<{ min: number; max: number }>({ min: xMin, max: xMax });
  // Ids of the plot lines we have added, so we can remove exactly those before
  // re-adding the current set (never touching any chart-owned lines).
  const managedPlotLineIdsRef = useRef<Set<string>>(new Set());
  // Same idea for the weekend/holiday shading bands.
  const managedPlotBandIdsRef = useRef<Set<string>>(new Set());
  // Charts that already carry the navigator stick-guard listener (step 1b).
  // WeakSet: a remounted chart (new instance) gets its own listener, a destroyed
  // one drops out on its own.
  const stickGuardedChartsRef = useRef<WeakSet<Highcharts.Chart>>(new WeakSet());

  useLayoutEffect(() => {
    const chart = chartRef.current?.chart;
    const series = chart?.series?.[0];
    const xAxis = chart?.xAxis?.[0];
    if (!chart || !series || !xAxis) return;

    // 1) Data. setData replaces the series points in place. It does NOT run the
    //    yAxis init path, so the treegrid's beforeRedraw listener is not
    //    re-registered. `false` defers the redraw to step 4 (one redraw total).
    series.setData(
      seriesData as unknown as Highcharts.PointOptionsType[],
      false,
    );

    // 1b) BUG-2026-0207 (rework): keep the navigator from snapping the window
    //     to the DATA span. During the redraw in step 4, Chart.redraw fires the
    //     dirty series' `updatedData` event; Highcharts Stock's navigator
    //     handler then decides to "stick" to the data min and/or max whenever
    //     the CURRENT window already reaches past them (stickToMin:
    //     axis.min <= dataMin; stickToMax: the navigator handle sits at the
    //     right edge), and its `foundExtremes` hook later in the same redraw
    //     overwrites axis.userMin/userMax with dataMin/dataMax — the axis
    //     silently becomes the data span (or slides to start at dataMin). A
    //     Lab Section / Machine filter produces exactly that state (every
    //     remaining bar inside the window), so "Next 3 months" + Rock rendered
    //     as a five-week strip. The window is OURS — xMin/xMax, or the user's
    //     saved zoom below — never the data's. `navigator.stickToMax: false`
    //     is a documented option (set in ScheduleGantt); stickToMin has none,
    //     so register ONE listener per chart on the same `updatedData` event.
    //     Listeners run in registration order, so this fires right after the
    //     navigator's handler and clears both flags before foundExtremes reads
    //     them. Resetting the flags here, before redraw, is too early — the
    //     handler would simply set them again.
    const navigator = (chart as ChartWithNavigator).navigator;
    if (navigator && !stickGuardedChartsRef.current.has(chart)) {
      stickGuardedChartsRef.current.add(chart);
      Highcharts.addEvent(series, 'updatedData', () => {
        navigator.stickToMin = false;
        navigator.stickToMax = false;
      });
    }

    // 2) Plot lines — reconcile by stable id. Using add/remove (instead of
    //    xAxis.update({ plotLines })) avoids destroy+init of the xAxis and the
    //    label flicker that comes with it.
    const managed = managedPlotLineIdsRef.current;
    for (const id of managed) {
      xAxis.removePlotLine(id);
    }
    managed.clear();
    for (const pl of plotLines) {
      xAxis.addPlotLine(pl);
      if (typeof pl.id === 'string') {
        managed.add(pl.id);
      }
    }

    // 2b) Plot bands (weekend + holiday shading) — same reconcile-by-id dance.
    //     addPlotBand/removePlotBand update in place without re-initialising the
    //     axis, so the treegrid beforeRedraw listener is never re-stacked.
    const managedBands = managedPlotBandIdsRef.current;
    for (const id of managedBands) {
      xAxis.removePlotBand(id);
    }
    managedBands.clear();
    for (const pb of plotBands) {
      xAxis.addPlotBand(pb);
      if (typeof pb.id === 'string') {
        managedBands.add(pb.id);
      }
    }

    // 3) Range / zoom.
    //    - Genuine range change → snap to the new window, drop saved zoom.
    //    - Same range (data refresh) → leave extremes alone, and re-apply the
    //      user's saved navigator zoom if Highcharts drifted it.
    const rangeChanged =
      prevRangeRef.current.min !== xMin || prevRangeRef.current.max !== xMax;
    if (rangeChanged) {
      prevRangeRef.current = { min: xMin, max: xMax };
      userExtremesRef.current = null;
      // redraw=false, animation=false: the view snaps; step 4 animates the bars.
      xAxis.setExtremes(xMin, xMax, false, false);
    } else {
      const saved = userExtremesRef.current;
      if (saved) {
        const cur = xAxis.getExtremes();
        if (cur.min !== saved.min || cur.max !== saved.max) {
          // `trigger: 'refresh'` tells our afterSetExtremes handler this is not
          // a user gesture, so it won't re-capture these extremes.
          xAxis.setExtremes(saved.min, saved.max, false, false, {
            trigger: 'refresh',
          } as Highcharts.AxisSetExtremesEventObject);
        }
      }
    }

    // 4) Height + a single animated redraw.
    //    BUG-2026-0194 (fixed rows): hand the viewport to the controller and
    //    redraw — its chart `render` hook then measures the VISIBLE rows (the
    //    new data must be laid out first) and sets the scrollable-plot-area
    //    minHeight / chart height in one setSize. No chart.update() anywhere.
    //    Legacy: setSize redraws (animated) when the height actually changed;
    //    otherwise redraw() animates the data change.
    if (rowHeightController) {
      rowHeightController.setViewportHeight(height);
      chart.redraw();
    } else if (chart.chartHeight !== height) {
      chart.setSize(undefined, height);
    } else {
      chart.redraw();
    }
  }, [chartRef, seriesData, xMin, xMax, plotLines, plotBands, height, rowHeightController, userExtremesRef]);
}
